/**
 * QZ Tray Printing Utility
 * Handles browser-based ESC/POS printing via QZ Tray.
 *
 * IMPORTANT: connectQZ() is a singleton. It must only be invoked when
 * actually needed (admin clicks "Connect Printer", or right before printing).
 * Never call it on every render, poll cycle, or page mount — doing so will
 * spam the QZ Tray "Allow/Block" permission popup on every refresh.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PRINTER_NAME_KEY = 'qz_printer_name';

// QZ Tray connection state — module-level singleton.
let qzConnected = false;
let qzConnectionPromise = null;
let qzSecurityWired = false;

/**
 * Load QZ Tray library dynamically. Idempotent — safe to call repeatedly.
 */
export const loadQZTray = () => {
  return new Promise((resolve, reject) => {
    if (window.qz) {
      // Ensure security promises are wired even on warm re-entry.
      setupQZSecurity();
      resolve(window.qz);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/qz-tray@2.2.4/qz-tray.js';
    script.async = true;
    script.onload = () => {
      if (window.qz) {
        setupQZSecurity();
        resolve(window.qz);
      } else {
        reject(new Error('QZ Tray failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load QZ Tray script'));
    document.head.appendChild(script);
  });
};

/**
 * Setup QZ Tray security (certificate and signing).
 * Wired exactly once per page lifetime — re-wiring on every call is unnecessary
 * but harmless. We guard with a flag to keep things tidy.
 */
const setupQZSecurity = () => {
  if (!window.qz || qzSecurityWired) return;

  // CRITICAL: QZ Tray defaults to SHA1 for signature verification. Our backend
  // signs with SHA512 (see /api/qz/sign in backend/server.py). If we don't set
  // this, every print request fails verification and QZ Tray shows the
  // "Untrusted website / Signature: Missing" prompt for each print — which
  // breaks auto-printing entirely.
  if (typeof window.qz.security.setSignatureAlgorithm === 'function') {
    window.qz.security.setSignatureAlgorithm('SHA512');
  }

  // Certificate promise
  window.qz.security.setCertificatePromise(function(resolve, reject) {
    fetch(`${API_URL}/api/qz/certificate`)
      .then(response => {
        if (!response.ok) throw new Error(`Cert fetch failed: ${response.status}`);
        return response.text();
      })
      .then(cert => {
        if (!cert || !cert.includes('BEGIN CERTIFICATE')) {
          throw new Error('QZ certificate response was empty or malformed');
        }
        resolve(cert);
      })
      .catch(err => {
        console.error('QZ certificate fetch failed:', err);
        reject(err);
      });
  });

  // Signature promise
  window.qz.security.setSignaturePromise(function(toSign) {
    return function(resolve, reject) {
      fetch(`${API_URL}/api/qz/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request: toSign })
      })
        .then(response => {
          if (!response.ok) throw new Error(`Sign endpoint returned ${response.status}`);
          return response.json();
        })
        .then(data => {
          if (!data?.signature) throw new Error('Sign endpoint returned no signature');
          resolve(data.signature);
        })
        .catch(err => {
          console.error('QZ signing failed:', err);
          reject(err);
        });
    };
  });

  qzSecurityWired = true;
};

/**
 * Connect to QZ Tray.
 *
 * Singleton behaviour:
 *   - If the websocket is already active → returns true immediately (no popup).
 *   - If a connect is already in-flight → returns the same promise.
 *   - Otherwise opens exactly one websocket. QZ Tray's "Allow/Block" popup
 *     will appear at most once per page lifetime; if the user clicked
 *     "Remember this decision" previously, QZ Tray will auto-accept.
 *
 * Throws a friendly error if QZ Tray is not running on the local machine.
 */
export const connectQZ = async () => {
  // Already connected → no-op (most common case after first successful connect).
  if (window.qz?.websocket?.isActive?.()) {
    qzConnected = true;
    return true;
  }

  // A connect is already pending → reuse that promise.
  if (qzConnectionPromise) {
    return qzConnectionPromise;
  }

  qzConnectionPromise = (async () => {
    try {
      await loadQZTray();
      if (!window.qz) {
        throw new Error('QZ Tray library not loaded');
      }
      // Double-check inside the promise to avoid a race with another caller.
      if (!window.qz.websocket.isActive()) {
        await window.qz.websocket.connect();
      }
      qzConnected = true;
      console.log('QZ Tray connected');
      return true;
    } catch (error) {
      qzConnected = false;
      console.error('QZ Tray connection failed:', error);
      // Friendlier error so UI can render a helpful message.
      const msg = String(error?.message || error);
      if (/closed|refused|connect/i.test(msg)) {
        throw new Error('QZ Tray is not running on this computer. Please start QZ Tray and try again.');
      }
      throw error;
    } finally {
      qzConnectionPromise = null;
    }
  })();

  return qzConnectionPromise;
};

/**
 * Disconnect from QZ Tray
 */
export const disconnectQZ = async () => {
  if (window.qz?.websocket?.isActive()) {
    await window.qz.websocket.disconnect();
  }
  qzConnected = false;
};

/**
 * Check if QZ Tray is connected
 */
export const isQZConnected = () => {
  return window.qz?.websocket?.isActive() || false;
};

/**
 * Persist the chosen printer name in localStorage so it survives refreshes
 * without an extra backend round-trip.
 */
export const setSavedPrinterName = (name) => {
  if (name) {
    localStorage.setItem(PRINTER_NAME_KEY, name);
  } else {
    localStorage.removeItem(PRINTER_NAME_KEY);
  }
};

export const getSavedPrinterName = () => {
  return localStorage.getItem(PRINTER_NAME_KEY) || null;
};

/**
 * Get list of available printers
 */
export const getPrinters = async () => {
  await connectQZ();
  return await window.qz.printers.find();
};

/**
 * Get default printer
 */
export const getDefaultPrinter = async () => {
  await connectQZ();
  return await window.qz.printers.getDefault();
};

// ============================================================================
// THERMAL RECEIPT FORMATTING
// ----------------------------------------------------------------------------
// Target printers:
//   - Epson TM-T20IV (network / QZ Tray path) — 80 mm roll
//   - Proline 408P built-in head             — 80 mm roll
// 80 mm at ESC/POS Font A gives 48 columns. All output is hard-capped to that.
// If you need to switch back to a 58 mm roll (32 columns), change ONE line
// below and everything reflows.
// ============================================================================

const RECEIPT_WIDTH = 48;
const SEPARATOR = '-'.repeat(RECEIPT_WIDTH);
const HEAVY_SEPARATOR = '='.repeat(RECEIPT_WIDTH);

/**
 * Split a string into lines that fit within `maxWidth` characters.
 * - Wraps on word boundaries when possible.
 * - Falls back to hard-splitting a single oversized word.
 * - Optional `hangingIndent` is prepended to every continuation line so
 *   wrapped content visually aligns under the first line (e.g. modifier rows).
 */
export const wrapText = (text, maxWidth = RECEIPT_WIDTH, hangingIndent = '') => {
  if (!text) return [''];
  const result = [];
  const paragraphs = String(text).split(/\r?\n/);

  for (const paragraph of paragraphs) {
    if (paragraph === '') {
      result.push('');
      continue;
    }

    // Preserve leading whitespace as a prefix that lives on the first wrapped line.
    const leadingMatch = paragraph.match(/^[ \t]+/);
    const leadingWS = leadingMatch ? leadingMatch[0] : '';
    const body = paragraph.slice(leadingWS.length);

    let current = leadingWS;
    const words = body.split(/\s+/).filter(Boolean);
    let isFirstWord = true;

    for (const word of words) {
      // Hard-split an oversized word.
      if (word.length > maxWidth - (current === leadingWS ? leadingWS.length : 0)) {
        if (current && current !== leadingWS) {
          result.push(current);
          current = hangingIndent;
        } else if (current === leadingWS) {
          // First word too long even with the leading indent — split it raw.
        }
        let remaining = word;
        const headPrefix = isFirstWord ? leadingWS : hangingIndent;
        const headSpace = maxWidth - headPrefix.length;
        if (headSpace > 0 && remaining.length > 0) {
          result.push(headPrefix + remaining.slice(0, headSpace));
          remaining = remaining.slice(headSpace);
        }
        while (remaining.length > maxWidth - hangingIndent.length) {
          const space = maxWidth - hangingIndent.length;
          result.push(hangingIndent + remaining.slice(0, space));
          remaining = remaining.slice(space);
        }
        current = remaining ? hangingIndent + remaining : '';
        isFirstWord = false;
        continue;
      }

      const candidate = current && current !== leadingWS
        ? `${current} ${word}`
        : `${current}${word}`;

      if (candidate.length > maxWidth) {
        if (current) result.push(current);
        current = `${hangingIndent}${word}`;
      } else {
        current = candidate;
      }
      isFirstWord = false;
    }

    if (current) result.push(current);
  }

  return result.length ? result : [''];
};

/**
 * Render a left/right line that fits inside the receipt width.
 * - If `left + 1 + right` fits → pad with spaces between them.
 * - Otherwise: wrap the left side, put the right value on its own right-aligned line.
 */
const formatLineLR = (left, right, width = RECEIPT_WIDTH) => {
  const rightStr = String(right ?? '');
  const leftStr = String(left ?? '');
  const out = [];

  // Single-line case.
  if (leftStr.length + 1 + rightStr.length <= width) {
    const gap = ' '.repeat(width - leftStr.length - rightStr.length);
    out.push(`${leftStr}${gap}${rightStr}`);
    return out;
  }

  // Multi-line: wrap the left, then right-align the value on a new line.
  const leftLines = wrapText(leftStr, width);
  out.push(...leftLines);
  out.push(rightStr.padStart(width));
  return out;
};

/**
 * Format an order for ESC/POS thermal printing on a 58 mm / 32-col printer.
 * Reference: https://reference.epson-biz.com/modules/ref_escpos/index.php
 *
 * Layout mirrors the printed receipt the user shipped on 26-Jun-2026:
 *   ┌─ DELIVERY (big bold left-aligned header)
 *   │  Customer name / address / instructions / phone
 *   ├─ items with bullet-list modifiers, dashed separators between
 *   ├─ SUB TOTAL / SERVICE CHARGE / DELIVERY CHARGE / TOTAL (caps, bold)
 *   ├─ PAID (centered)
 *   ├─ PRE-ORDER HH:MM dd/mm/yy (reverse-video full-width bar)  ← preorders only
 *   ├─ Placed / Deliver by / Order through ...
 *   └─ Restaurant footer
 */
export const formatReceipt = (order, restaurantName = 'Crispy Chicken Forfar') => {
  const ESC = '\x1B';
  const GS = '\x1D';
  const LF = '\x0A';
  const out = [];

  // ---- Helpers --------------------------------------------------------
  const boldOn = () => out.push(ESC + 'E' + '\x01');
  const boldOff = () => out.push(ESC + 'E' + '\x00');
  const alignLeft = () => out.push(ESC + 'a' + '\x00');
  const alignCenter = () => out.push(ESC + 'a' + '\x01');
  // ESC ! n bitmask: 0x10 = double height, 0x20 = double width, 0x30 = both.
  const sizeNormal = () => out.push(ESC + '!' + '\x00');
  const sizeDoubleH = () => out.push(ESC + '!' + '\x10');
  const sizeDoubleHW = () => out.push(ESC + '!' + '\x30');
  // GS B n — reverse video (white on black). Most ESC/POS thermal printers
  // invert the *entire cell* (text + spaces), giving a true black bar.
  const reverseOn = () => out.push(GS + 'B' + '\x01');
  const reverseOff = () => out.push(GS + 'B' + '\x00');

  // Render `amount` with no currency symbol, right-aligned within RECEIPT_WIDTH
  // alongside the left label. e.g. ("TOTAL", 27.72) → "TOTAL                     27.72".
  const lineWithAmount = (left, amount) => {
    const right = Number(amount || 0).toFixed(2);
    formatLineLR(left, right, RECEIPT_WIDTH).forEach((l) => out.push(l + LF));
  };

  // Format a date in UK style (no comma between date and time).
  const fmtDateTime = (d) => {
    const dt = new Date(d);
    const datePart = dt.toLocaleDateString('en-GB', {
      day: '2-digit', month: '2-digit', year: '2-digit',
    });
    const timePart = dt.toLocaleTimeString('en-GB', {
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    return `${datePart} ${timePart}`;
  };

  // ---- 1. Printer init ----------------------------------------------
  out.push(ESC + '@');                          // Initialize
  out.push(ESC + 't' + '\x13');                 // PC858 codepage (supports £)
  out.push(ESC + 'M' + '\x00');                 // Font A (32 cols @ 58 mm)
  alignLeft();

  const orderTypeUpper = (order.order_type || 'delivery').toUpperCase();
  const orderTypeLower = orderTypeUpper.toLowerCase();
  const isDelivery = orderTypeLower === 'delivery';

  // ---- 2. Header: DELIVERY / COLLECTION  ----------------------------
  boldOn();
  sizeDoubleHW();
  out.push(orderTypeUpper + LF);
  sizeNormal();
  boldOff();

  // Order number — matches the admin dashboard label (#001 daily counter,
  // falling back to the last 6 chars of the order id).
  const orderNo = order.daily_order_number
    ? `#${String(order.daily_order_number).padStart(3, '0')}`
    : (order.id ? `#${String(order.id).slice(-6).toUpperCase()}` : '');
  if (orderNo) {
    boldOn();
    sizeDoubleH();
    out.push(`ORDER ${orderNo}` + LF);
    sizeNormal();
    boldOff();
  }
  out.push(LF);

  // ---- 3. Customer block (name / address / instructions / phone) ----
  if (order.customer_name) {
    wrapText(order.customer_name).forEach((l) => out.push(l + LF));
  }
  if (isDelivery) {
    if (order.delivery_address) {
      const parts = String(order.delivery_address)
        .split(/\n|,/)
        .map((p) => p.trim())
        .filter(Boolean);
      const postcode = (order.postcode || '').trim();
      if (postcode && parts.length && !parts[parts.length - 1].toUpperCase().includes(postcode.toUpperCase())) {
        parts[parts.length - 1] = `${parts[parts.length - 1]} ${postcode}`;
      }
      parts.forEach((p) =>
        wrapText(p, RECEIPT_WIDTH).forEach((l) => out.push(l + LF))
      );
    } else if (order.postcode) {
      out.push(order.postcode + LF);
    }
  }
  if (order.customer_phone) {
    out.push(order.customer_phone + LF);
  }

  // Kitchen / delivery instructions — visually isolated so chefs cannot miss
  // them: own dashed block, bold, double-height text. The label sits on its
  // own reverse-video bar so it acts as a clear divider in a busy ticket.
  if (order.notes) {
    out.push(SEPARATOR + LF);
    const label = isDelivery ? 'DELIVERY INSTRUCTIONS' : 'COLLECTION INSTRUCTIONS';
    const padded = ` ${label} `;
    const remaining = Math.max(0, RECEIPT_WIDTH - padded.length);
    const lp = Math.floor(remaining / 2);
    const rp = remaining - lp;
    reverseOn();
    out.push(' '.repeat(lp) + padded + ' '.repeat(rp) + LF);
    reverseOff();
    boldOn();
    sizeDoubleH();
    wrapText(order.notes, RECEIPT_WIDTH).forEach((l) =>
      out.push(l + LF)
    );
    sizeNormal();
    boldOff();
  }

  // ---- 4. Items -----------------------------------------------------
  out.push(SEPARATOR + LF);

  const items = order.items || [];
  items.forEach((item, idx) => {
    const qty = item.quantity || 1;
    const unitPrice = Number(item.price || 0);
    const lineTotal = unitPrice * qty;
    const isBundle = (item.item_type || 'standard') === 'bundle';
    // For bundles, prefer the admin-configured kitchen heading (e.g.
    // "MEAL FOR 2"). Falls back to the customer-facing name.
    const name = isBundle && item.kitchen_name ? item.kitchen_name : (item.name || 'Unknown Item');

    // Item name (bold) on the left, line total (no £) right-aligned.
    boldOn();
    formatLineLR(`${qty}x ${name}`, lineTotal.toFixed(2)).forEach((l) =>
      out.push(l + LF)
    );
    boldOff();

    if (isBundle) {
      // Bundle rendering — group selections by person label for legibility.
      const selections = Array.isArray(item.bundle_selections) ? item.bundle_selections : [];
      const grouped = new Map();
      selections.forEach((s) => {
        const key = s.person_label || '__solo__';
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(s);
      });
      grouped.forEach((picks, label) => {
        if (label !== '__solo__') {
          boldOn();
          wrapText(String(label).toUpperCase(), RECEIPT_WIDTH, '').forEach((l) =>
            out.push(l + LF),
          );
          boldOff();
        }
        picks.forEach((p) => {
          const dispName = p.display_name || 'Selection';
          const surcharge = Number(p.unit_surcharge || 0);
          if (surcharge > 0) {
            formatLineLR(`  \xB7 ${dispName}`, `+${surcharge.toFixed(2)}`).forEach((l) =>
              out.push(l + LF),
            );
          } else {
            wrapText(`  \xB7 ${dispName}`, RECEIPT_WIDTH, '    ').forEach((l) =>
              out.push(l + LF),
            );
          }
          // Nested modifier group choices, e.g. Sauces / Salad / Extras.
          const nested = p.nested_customizations || {};
          Object.values(nested).forEach((choices) => {
            const arr = Array.isArray(choices) ? choices : [choices];
            arr.forEach((c) => {
              const nm = typeof c === 'object' ? c?.name : String(c);
              if (!nm) return;
              const pr = typeof c === 'object' && Number(c?.price) > 0 ? Number(c.price) : 0;
              const leftCol = `    - ${nm}`;
              if (pr > 0) {
                formatLineLR(leftCol, `+${pr.toFixed(2)}`).forEach((l) =>
                  out.push(l + LF),
                );
              } else {
                wrapText(leftCol, RECEIPT_WIDTH, '      ').forEach((l) =>
                  out.push(l + LF),
                );
              }
            });
          });
        });
      });
      // Fixed included items go below the selections, no price shown (they
      // are already priced into the bundle base).
      const fixed = Array.isArray(item.fixed_items_snapshot) ? item.fixed_items_snapshot : [];
      if (fixed.length) {
        boldOn();
        wrapText('INCLUDED', RECEIPT_WIDTH, '').forEach((l) => out.push(l + LF));
        boldOff();
        fixed.forEach((fx) => {
          const line = `  \xB7 ${Number(fx.quantity || 1)}x ${fx.name || 'Included'}`;
          wrapText(line, RECEIPT_WIDTH, '    ').forEach((l) => out.push(l + LF));
        });
      }
    } else {
      // Standard modifiers as bulleted indented lines. Middle-dot "·"
      // (CP858 0xFA) is used in place of "•" (U+2022) which is NOT in
      // CP858 and would print as "?" on the thermal printer.
      const cust = item.customizations || {};
      Object.values(cust).forEach((value) => {
        const arr = Array.isArray(value) ? value : [value];
        arr.forEach((v) => {
          if (v === null || v === undefined || v === '') return;
          const optName = typeof v === 'object' ? v.name : String(v);
          const optPrice = typeof v === 'object' && Number(v.price) > 0 ? Number(v.price) : 0;
          if (!optName) return;
          const leftCol = `  \xB7 ${optName}`;
          if (optPrice > 0) {
            formatLineLR(leftCol, `+${optPrice.toFixed(2)}`).forEach((l) =>
              out.push(l + LF)
            );
          } else {
            wrapText(leftCol, RECEIPT_WIDTH, '    ').forEach((l) =>
              out.push(l + LF)
            );
          }
        });
      });
    }

    // Dashed separator between items (not after the last one).
    if (idx < items.length - 1) {
      out.push(SEPARATOR + LF);
    }
  });

  // ---- 5. Totals (uppercase, bold) ----------------------------------
  // Separator above the totals block so it's clearly distinct from the
  // last item row.
  out.push(SEPARATOR + LF);
  const subtotal = Number(order.subtotal ?? 0);
  const service = Number(order.service_charge ?? 0);
  const deliveryFee = Number(order.delivery_fee ?? 0);
  const totalAmt = Number(order.total_amount ?? 0);

  boldOn();
  if (subtotal > 0) {
    lineWithAmount('SUB TOTAL', subtotal);
  }
  if (service > 0) {
    lineWithAmount('SERVICE CHARGE', service);
  }
  if (isDelivery) {
    if (deliveryFee > 0) {
      lineWithAmount('DELIVERY CHARGE', deliveryFee);
    } else {
      formatLineLR('DELIVERY CHARGE', 'FREE', RECEIPT_WIDTH).forEach((l) =>
        out.push(l + LF)
      );
    }
  } else if (deliveryFee > 0) {
    lineWithAmount('DELIVERY CHARGE', deliveryFee);
  }
  lineWithAmount('TOTAL', totalAmt);
  boldOff();

  // ---- 6. Payment status (centered, bold) ---------------------------
  out.push(SEPARATOR + LF);

  const paymentMethod = (order.payment_method || '').toLowerCase();
  const paymentStatus = (order.payment_status || '').toUpperCase();
  const isCOC = paymentMethod === 'coc';
  const isCOD = paymentMethod === 'cod';
  const isCashPending = isCOD || isCOC || paymentStatus === 'COD_PENDING';
  const isPaid = !isCashPending && (
    paymentStatus === 'PAID' ||
    paymentStatus === 'SUCCEEDED' ||
    paymentStatus === 'COMPLETED' ||
    paymentMethod === 'card' ||
    paymentMethod === 'stripe' ||
    !!order.paid_at
  );

  alignCenter();
  boldOn();
  if (isCashPending) {
    out.push((isCOC ? 'COC - PAY ON COLLECTION' : 'COD - PAY ON DELIVERY') + LF);
  } else if (isPaid) {
    out.push('PAID' + LF);
  } else if (paymentStatus) {
    out.push(paymentStatus + LF);
  }
  boldOff();

  // ---- 7. PRE-ORDER bar (reverse video) — preorders only ------------
  if (order.is_preorder && order.scheduled_for) {
    const sched = new Date(order.scheduled_for);
    const timePart = sched.toLocaleTimeString('en-GB', {
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const datePart = sched.toLocaleDateString('en-GB', {
      day: '2-digit', month: '2-digit', year: '2-digit',
    });
    const barText = `PRE-ORDER ${timePart} ${datePart}`;
    const padded = ` ${barText} `;
    const remaining = Math.max(0, RECEIPT_WIDTH - padded.length);
    const lp = Math.floor(remaining / 2);
    const rp = remaining - lp;
    reverseOn();
    out.push(' '.repeat(lp) + padded + ' '.repeat(rp) + LF);
    reverseOff();
  }
  alignLeft();

  // ---- 8. Timeline (Placed / Deliver by / Order through) ------------
  const placedAt = order.created_at || order.uk_order_time || order.paid_at;
  if (placedAt) {
    out.push(`Placed: ${fmtDateTime(placedAt)}` + LF);
  }
  if (order.is_preorder && order.scheduled_for) {
    const label = isDelivery ? 'Deliver by' : 'Collect by';
    out.push(`${label}: ${fmtDateTime(order.scheduled_for)}` + LF);
  }
  out.push('Order through: Online - Website' + LF);

  // ---- 9. Restaurant footer -----------------------------------------
  out.push(LF);
  alignCenter();
  out.push(SEPARATOR + LF);
  wrapText(restaurantName).forEach((l) => out.push(l + LF));
  out.push('3-5 Queen St, Forfar' + LF);
  out.push('01307 477786' + LF);

  // ---- 10. Feed + cut -----------------------------------------------
  out.push(ESC + 'd' + '\x03');                 // feed 3 lines
  out.push(GS + 'V' + '\x42' + '\x03');         // partial cut

  return out.join('');
};

/**
 * Print an order receipt.
 * Lazily ensures a QZ Tray connection — connects only if not already active.
 */
export const printReceipt = async (order, printerName) => {
  const name = printerName || getSavedPrinterName();
  if (!name) {
    throw new Error('No printer selected. Please pick a printer in Settings first.');
  }

  await connectQZ();

  const config = window.qz.configs.create(name, {
    encoding: 'CP858'  // Codepage 858 supports £ and €. Matches ESC t 19 in formatReceipt.
  });

  const receiptData = formatReceipt(order);

  // Send as raw ESC/POS commands
  const data = [{
    type: 'raw',
    format: 'command',
    data: receiptData,
    options: { language: 'ESCPOS' }
  }];

  await window.qz.print(config, data);

  return true;
};

/**
 * Test print a sample receipt.
 * Deliberately includes long item names, multiple modifiers, a long delivery
 * address and a multi-figure total so the operator can confirm nothing
 * truncates on 58 mm paper.
 */
export const testPrint = async (printerName) => {
  const testOrder = {
    id: 'TESTABC123',
    daily_order_number: 99,
    customer_name: 'Mr Test Customer Withaverylongnameindeed',
    customer_phone: '07123 456 789',
    customer_email: 'long.email.address@example.co.uk',
    order_type: 'delivery',
    delivery_address: 'Flat 4B, 123 Lemon Tree Avenue\nForfar Industrial Estate',
    postcode: 'DD8 1AA',
    payment_method: 'cod',
    items: [
      {
        name: 'Crispy Chicken Wrap Meal Deluxe',
        quantity: 2,
        price: 9.19,
        customizations: {
          extras: [
            { name: 'Extra Cheese', price: 0.50 },
            { name: 'Standard Fries (Large)', price: 0.00 }
          ],
          sauce: { name: 'BBQ Sauce', price: 0.30 }
        }
      },
      {
        name: 'Family Bargain Bucket with 8 wings & 4 sides',
        quantity: 1,
        price: 24.99,
        customizations: { drinks: { name: 'Pepsi Max 1.5L', price: 0.00 } }
      },
      { name: 'Garlic Mayo Dip', quantity: 3, price: 0.50 }
    ],
    subtotal: 44.87,
    service_charge: 0.75,
    delivery_fee: 2.50,
    total_amount: 48.12,
    paid_at: new Date().toISOString(),
    notes: 'Please ring the buzzer on arrival — door is round the back.',
    is_preorder: false
  };

  return await printReceipt(testOrder, printerName);
};

export default {
  loadQZTray,
  connectQZ,
  disconnectQZ,
  isQZConnected,
  getPrinters,
  getDefaultPrinter,
  getSavedPrinterName,
  setSavedPrinterName,
  formatReceipt,
  wrapText,
  printReceipt,
  testPrint
};
